import { useState, useEffect, useMemo } from 'react';
import {
  AdminAnalyticsOverview,
  AdminEngagementMetrics,
  AdminBusinessMetrics,
  AdminRevenueMetrics,
  UserGrowthDataPoint,
  HomeCreationDataPoint,
  RevenueDataPoint,
  SubscriptionTierDataPoint,
  FeatureUsageData,
  CohortRetentionData,
  BusinessPerformanceData,
  ActivityHeatmapData,
  DateRange,
  AdminAnalyticsData,
} from '../../types/admin';
import { subDays, format } from 'date-fns';

/**
 * useAdminAnalytics - Hook for analytics data
 *
 * Generates realistic mock data for:
 * - Overview metrics (DAU, MAU, MRR, churn)
 * - User engagement analytics
 * - Business performance metrics
 * - Revenue analytics with trends
 * - Cohort retention analysis
 * - Feature usage statistics
 *
 * All data is consistent and tells a story of platform growth
 */

// Helper function to generate random value with variance
function randomWithVariance(base: number, variance: number): number {
  return Math.round(base + (Math.random() - 0.5) * 2 * variance);
}

// Helper function to add growth trend to data
function applyGrowthTrend(base: number, dayIndex: number, growthRate: number): number {
  const growth = 1 + (growthRate * dayIndex) / 100;
  return Math.round(base * growth);
}

// Generate user growth data (90 days)
function generateUserGrowthData(days: number = 90): UserGrowthDataPoint[] {
  const data: UserGrowthDataPoint[] = [];
  const endDate = new Date();

  let totalUsers = 8500; // Starting user base
  const baseNewUsers = 80; // Base new users per day
  const baseDau = 1200;
  const baseWau = 3500;
  const baseMau = 9500;
  const growthRate = 0.15; // 15% growth over 90 days

  for (let i = days - 1; i >= 0; i--) {
    const date = format(subDays(endDate, i), 'yyyy-MM-dd');
    const dayIndex = days - i;

    // Calculate new users with growth trend and variance
    const newUsers = randomWithVariance(
      applyGrowthTrend(baseNewUsers, dayIndex, growthRate),
      15
    );
    totalUsers += newUsers;

    // DAU, WAU, MAU also grow with some weekly patterns
    const dayOfWeek = subDays(endDate, i).getDay();
    const weekendFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.85 : 1.0;

    data.push({
      date,
      total_users: totalUsers,
      new_users: newUsers,
      dau: randomWithVariance(
        applyGrowthTrend(baseDau, dayIndex, growthRate) * weekendFactor,
        150
      ),
      wau: randomWithVariance(applyGrowthTrend(baseWau, dayIndex, growthRate), 300),
      mau: randomWithVariance(applyGrowthTrend(baseMau, dayIndex, growthRate), 500),
    });
  }

  return data;
}

// Generate home creation data (90 days)
function generateHomeCreationData(days: number = 90): HomeCreationDataPoint[] {
  const data: HomeCreationDataPoint[] = [];
  const endDate = new Date();

  let cumulativeHomes = 15000;
  const baseHomesPerDay = 45;
  const growthRate = 0.12; // 12% growth

  for (let i = days - 1; i >= 0; i--) {
    const date = format(subDays(endDate, i), 'yyyy-MM-dd');
    const dayIndex = days - i;

    const dayOfWeek = subDays(endDate, i).getDay();
    const weekendFactor = dayOfWeek === 0 || dayOfWeek === 6 ? 0.7 : 1.0;

    const homesCreated = randomWithVariance(
      applyGrowthTrend(baseHomesPerDay, dayIndex, growthRate) * weekendFactor,
      10
    );
    cumulativeHomes += homesCreated;

    data.push({
      date,
      homes_created: homesCreated,
      cumulative_homes: cumulativeHomes,
    });
  }

  return data;
}

// Generate revenue data (90 days)
function generateRevenueData(days: number = 90): RevenueDataPoint[] {
  const data: RevenueDataPoint[] = [];
  const endDate = new Date();

  let baseMrr = 42000;
  const growthRate = 0.08; // 8% growth

  for (let i = days - 1; i >= 0; i--) {
    const date = format(subDays(endDate, i), 'yyyy-MM-dd');
    const dayIndex = days - i;

    const mrr = randomWithVariance(
      applyGrowthTrend(baseMrr, dayIndex, growthRate),
      1500
    );

    const newRevenue = randomWithVariance(mrr * 0.03, 200); // ~3% new MRR daily
    const churnedRevenue = randomWithVariance(mrr * 0.015, 100); // ~1.5% churn

    data.push({
      date,
      mrr,
      arr: mrr * 12,
      new_revenue: newRevenue,
      churned_revenue: churnedRevenue,
    });
  }

  return data;
}

// Generate subscription tier distribution (90 days)
function generateSubscriptionTierData(days: number = 90): SubscriptionTierDataPoint[] {
  const data: SubscriptionTierDataPoint[] = [];
  const endDate = new Date();

  let freeTier = 7500;
  let proTier = 2800;
  let agentTier = 450;

  for (let i = days - 1; i >= 0; i--) {
    const date = format(subDays(endDate, i), 'yyyy-MM-dd');

    // Free tier grows fastest
    freeTier += randomWithVariance(45, 10);
    // Pro tier grows steadily
    proTier += randomWithVariance(20, 5);
    // Agent tier grows slowly
    agentTier += randomWithVariance(3, 1);

    data.push({
      date,
      free: freeTier,
      pro: proTier,
      agent: agentTier,
    });
  }

  return data;
}

// Generate feature usage data
function generateFeatureUsageData(): FeatureUsageData[] {
  return [
    {
      feature: 'Home Management',
      usage_count: 285000,
      unique_users: 9800,
      percentage: 89,
    },
    {
      feature: 'Task Management',
      usage_count: 178000,
      unique_users: 7200,
      percentage: 65,
    },
    {
      feature: 'Agent-Client Chat',
      usage_count: 156000,
      unique_users: 3200,
      percentage: 29,
    },
    {
      feature: 'Photo Upload',
      usage_count: 125000,
      unique_users: 6500,
      percentage: 59,
    },
    {
      feature: 'Document Storage',
      usage_count: 98000,
      unique_users: 4800,
      percentage: 44,
    },
    {
      feature: 'Calendar/Events',
      usage_count: 76000,
      unique_users: 4200,
      percentage: 38,
    },
    {
      feature: 'Mobile App',
      usage_count: 68000,
      unique_users: 3800,
      percentage: 35,
    },
    {
      feature: 'Sharing',
      usage_count: 52000,
      unique_users: 3100,
      percentage: 28,
    },
  ];
}

// Generate cohort retention data
function generateCohortRetentionData(): CohortRetentionData[] {
  const data: CohortRetentionData[] = [];
  const endDate = new Date();

  // Generate 12 monthly cohorts
  for (let i = 11; i >= 0; i--) {
    const cohortDate = format(subDays(endDate, i * 30), 'yyyy-MM');
    const cohortSize = randomWithVariance(650, 100);

    // Retention drops over time, but recent cohorts retain better (product improvement)
    const retentionMultiplier = 1 - (i * 0.02); // Recent cohorts 2% better per month

    data.push({
      cohort_date: cohortDate,
      cohort_size: cohortSize,
      retention: {
        day_0: 100,
        day_7: Math.round(randomWithVariance(82, 3) * retentionMultiplier),
        day_14: Math.round(randomWithVariance(74, 3) * retentionMultiplier),
        day_30: Math.round(randomWithVariance(65, 3) * retentionMultiplier),
        day_60: Math.round(randomWithVariance(58, 3) * retentionMultiplier),
        day_90: Math.round(randomWithVariance(52, 3) * retentionMultiplier),
      },
    });
  }

  return data.reverse(); // Most recent first
}

// Generate top businesses data
function generateTopBusinessesData(): BusinessPerformanceData[] {
  const businesses = [
    'Luxury Realty Group',
    'Metro Housing Partners',
    'Coastal Properties Inc',
    'Summit Real Estate',
    'Downtown Brokers',
    'Elite Home Services',
    'Premier Property Group',
    'Urban Nest Realty',
    'Prestige Homes Network',
    'Golden Gate Properties',
  ];

  return businesses.map((name, index) => ({
    business_id: `business_${index + 1}`,
    business_name: name,
    revenue: randomWithVariance(8000 - index * 600, 500),
    clients_count: randomWithVariance(180 - index * 15, 20),
    agents_count: randomWithVariance(25 - index * 2, 3),
    growth_rate: randomWithVariance(15 - index * 1.2, 5),
  }));
}

// Generate activity heatmap data (hour x day of week)
function generateActivityHeatmapData(): ActivityHeatmapData[] {
  const data: ActivityHeatmapData[] = [];

  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      let baseActivity = 100;

      // Weekday vs weekend pattern
      if (day === 0 || day === 6) {
        baseActivity *= 0.6; // Weekend
      }

      // Hourly pattern (9am-5pm peak)
      if (hour >= 9 && hour <= 17) {
        baseActivity *= 2.5;
      } else if (hour >= 6 && hour <= 8) {
        baseActivity *= 1.5;
      } else if (hour >= 18 && hour <= 21) {
        baseActivity *= 1.8;
      } else if (hour >= 22 || hour <= 5) {
        baseActivity *= 0.3;
      }

      data.push({
        hour,
        day,
        activity_count: randomWithVariance(baseActivity, baseActivity * 0.2),
      });
    }
  }

  return data;
}

export function useAdminAnalytics(initialDateRange?: DateRange) {
  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange || {
      start: subDays(new Date(), 90),
      end: new Date(),
    }
  );
  const [isLoading, setIsLoading] = useState(false);

  // Generate all analytics data
  const analyticsData = useMemo<AdminAnalyticsData>(() => {
    const userGrowth = generateUserGrowthData(90);
    const homeCreation = generateHomeCreationData(90);
    const revenueHistory = generateRevenueData(90);
    const subscriptionTiers = generateSubscriptionTierData(90);

    // Get latest data points for current metrics
    const latestUserGrowth = userGrowth[userGrowth.length - 1];
    const latestRevenue = revenueHistory[revenueHistory.length - 1];
    const latestHomeCreation = homeCreation[homeCreation.length - 1];
    const latestSubscriptionTiers = subscriptionTiers[subscriptionTiers.length - 1];

    // Calculate metrics
    const totalActiveSubscriptions =
      latestSubscriptionTiers.pro + latestSubscriptionTiers.agent;
    const churnRate = 2.3; // ~2.3% monthly churn

    const overview: AdminAnalyticsOverview = {
      total_users: latestUserGrowth.total_users,
      dau: latestUserGrowth.dau,
      wau: latestUserGrowth.wau,
      mau: latestUserGrowth.mau,
      total_homes: latestHomeCreation.cumulative_homes,
      active_subscriptions: totalActiveSubscriptions,
      mrr: latestRevenue.mrr,
      churn_rate: churnRate,
    };

    const engagement: AdminEngagementMetrics = {
      dau: latestUserGrowth.dau,
      wau: latestUserGrowth.wau,
      mau: latestUserGrowth.mau,
      dau_mau_ratio: parseFloat(
        ((latestUserGrowth.dau / latestUserGrowth.mau) * 100).toFixed(1)
      ),
      avg_session_duration: 18.5, // minutes
      actions_per_user: 12.8,
    };

    const topBusinesses = generateTopBusinessesData();
    const business: AdminBusinessMetrics = {
      total_businesses: 285,
      active_businesses: 268,
      avg_agents_per_business: 8.4,
      avg_clients_per_business: 34.2,
      business_churn_rate: 1.8,
    };

    const revenue: AdminRevenueMetrics = {
      total_revenue: latestRevenue.arr * 2.5, // Lifetime revenue approximation
      mrr: latestRevenue.mrr,
      arr: latestRevenue.arr,
      arpu: parseFloat((latestRevenue.mrr / totalActiveSubscriptions).toFixed(2)),
      ltv: 1850, // Average customer lifetime value
      cac: 245, // Customer acquisition cost
      refund_rate: 0.8, // 0.8% refund rate
    };

    return {
      overview,
      engagement,
      business,
      revenue,
      userGrowth,
      homeCreation,
      revenueHistory,
      subscriptionTiers,
      featureUsage: generateFeatureUsageData(),
      cohortRetention: generateCohortRetentionData(),
      topBusinesses,
      activityHeatmap: generateActivityHeatmapData(),
      dateRange,
    };
  }, [dateRange]);

  // Simulate loading when date range changes
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [dateRange]);

  // Export data to CSV
  const exportToCSV = (tabName: string) => {
    let csvContent = '';
    const timestamp = format(new Date(), 'yyyy-MM-dd-HHmmss');

    switch (tabName) {
      case 'overview':
        csvContent = 'Metric,Value\n';
        csvContent += `Total Users,${analyticsData.overview.total_users}\n`;
        csvContent += `DAU,${analyticsData.overview.dau}\n`;
        csvContent += `MAU,${analyticsData.overview.mau}\n`;
        csvContent += `MRR,$${analyticsData.overview.mrr}\n`;
        break;

      case 'user-growth':
        csvContent = 'Date,Total Users,New Users,DAU,WAU,MAU\n';
        analyticsData.userGrowth.forEach((point) => {
          csvContent += `${point.date},${point.total_users},${point.new_users},${point.dau},${point.wau},${point.mau}\n`;
        });
        break;

      case 'revenue':
        csvContent = 'Date,MRR,ARR,New Revenue,Churned Revenue\n';
        analyticsData.revenueHistory.forEach((point) => {
          csvContent += `${point.date},${point.mrr},${point.arr},${point.new_revenue},${point.churned_revenue}\n`;
        });
        break;

      default:
        csvContent = 'Export not available for this section\n';
    }

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `analytics-${tabName}-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    analyticsData,
    isLoading,
    dateRange,
    setDateRange,
    exportToCSV,
  };
}
